import {
	GraphQLResultsPropertyOutput,
	GraphQLResultsTestSuiteOutput,
	GraphQLResultsTestCaseSummaryOutput,
	GraphQLResultsTestCaseStepOutput,
	GraphQLResultsObjectsUnderTestOutput,
	GraphQLResultsObjectUnderTestEcuOutput,
	GraphQLResultsObjectUnderTestOffboardComponentOutput,
	GraphQLResultsRequirementRowOutput,
	GraphQLResultsSopsOutput,
	GraphQLResultsEcuParameterOutput,
	GraphQLResultsTestSuiteAlternativeOutput,
	GraphQLResultsTestCaseAlternativeOutput,
	GraphQLResultsTestCaseDetailsOutput,
	GraphQLResultsTestCaseAlternativeRefOutput,
	GraphQLResultsVersionOutput,
	GraphQLResultsTestCaseUfOutput,
	GraphQLResultsTestCaseProductPropertyOutput,
	GraphQLResultsTestResultEcuOutput,
	GraphQLResultsTestResultOffboardComponentOutput,
	GraphQLResultsTestSuiteFullOutput,
	GraphQLResultsTestCaseFullOutput,
	GraphQLResultsTestSpecificationOutput,
} from 'src/app/graphql/models/output/results.output.model';
import {
	ResultsProperty,
	ResultsVersion,
	ResultsTestSuite,
	ResultsTestCase,
	ResultsTestCaseProductProperty,
	ResultsUF,
	ResultsEcu,
	ResultsOffboardComponent,
	ResultsTestCaseStep,
	ResultsTestCaseAlternative,
	ResultsTestResultEcu,
	ResultsTestResultOffboardComponent,
	ResultsRequirementRow,
	ResultsSopsResponse,
	ResultsTestSpecification,
	EcuParamItem,
} from 'src/app/models/results';

/**
 * Shared mappers for the smaller Results queries: raw GraphQL output → app-level models. The larger
 * queries (complete test run, test-run-to-history-entry, results-to-flat-document) keep their own
 * dedicated mapper files. Add more Results mappers here as endpoints gain typed mapping (see the
 * untyped `any[]` methods in results.service.ts — alternatives, requirements, ECU params/readouts).
 */

/**
 * Maps a raw GraphQL property (from resultsPropertiesForTestRun /
 * resultsPropertiesForTestResult) to the app-level ResultsProperty model.
 *
 * The backend exposes a UI-friendly `label` that, when present, is the name to show — this mapper
 * folds that into `name` so consumers can render `name` directly without a label/name fallback.
 * @param raw
 */
export function mapGraphQLResultsProperty(raw: GraphQLResultsPropertyOutput): ResultsProperty {
	return {
		uid: '',
		version: raw.version,
		validFrom: '',
		validTo: null,
		name: raw.label || raw.name,
		value: raw.value,
		label: raw.label,
		identifier: raw.identifier,
		variant: raw.variant,
		description: raw.description,
		valueDescription: raw.valueDescription,
		fieldFormat: raw.fieldFormat,
	};
}

export function mapGraphQLResultsProperties(raw: GraphQLResultsPropertyOutput[] | null | undefined): ResultsProperty[] {
	return (raw ?? []).map(mapGraphQLResultsProperty);
}

/**
 * Maps a raw child test suite (resultsTestSuitesForTestSuite) to the app-level ResultsTestSuite,
 * recursing into nested child suites for the tree. Detail-only fields (ufs, productProperties,
 * offboardComponents, versions) aren't selected by this query and default to empty.
 * @param raw
 */
export function mapGraphQLResultsTestSuite(raw: GraphQLResultsTestSuiteOutput): ResultsTestSuite {
	return {
		testSuiteUid: raw.testSuiteUid,
		itemNo: raw.itemNo,
		name: raw.name,
		version: raw.version,
		owners: raw.owners ?? [],
		ufs: [],
		productProperties: [],
		offboardComponents: [],
		regTime: raw.regTime,
		regBy: raw.regBy,
		description: raw.description,
		testSuites: (raw.testSuites ?? []).map(mapGraphQLResultsTestSuite),
		nodeOrder: raw.nodeOrder,
		versions: [],
	};
}

export function mapGraphQLResultsTestSuites(raw: GraphQLResultsTestSuiteOutput[] | null | undefined): ResultsTestSuite[] {
	return (raw ?? []).map(mapGraphQLResultsTestSuite);
}

/**
 * Maps a raw test case summary (resultsTestCasesForTestSuite) to the app-level ResultsTestCase.
 * Only summary fields are returned by this query; detail fields default to empty (use
 * resultsTestCaseDetails for the full record).
 * @param raw
 */
export function mapGraphQLResultsTestCaseSummary(raw: GraphQLResultsTestCaseSummaryOutput): ResultsTestCase {
	return {
		tcUid: raw.tcUid,
		itemNo: raw.itemNo,
		name: raw.name,
		description: '',
		version: raw.version,
		regBy: raw.regBy,
		regTime: raw.regTime,
		steps: [],
		owners: [],
		ufs: [],
		productProperties: [],
		ecus: [],
		offboardComponents: [],
		requirement: '',
		script: '',
		resultCount: 0,
		alternatives: [],
		versions: [],
		operator: raw.operator ?? '',
		value: raw.value ?? '',
		testCases: (raw.testCases ?? []).map(mapGraphQLResultsTestCaseSummary),
	};
}

export function mapGraphQLResultsTestCaseSummaries(raw: GraphQLResultsTestCaseSummaryOutput[] | null | undefined): ResultsTestCase[] {
	return (raw ?? []).map(mapGraphQLResultsTestCaseSummary);
}

/**
 * Maps a raw test case step (resultsTestCaseSteps) to the app-level ResultsTestCaseStep.
 * @param raw
 */
export function mapGraphQLResultsTestCaseStep(raw: GraphQLResultsTestCaseStepOutput): ResultsTestCaseStep {
	return {
		stepNo: raw.stepNo,
		precondition: raw.precondition,
		stimuli: raw.stimuli,
		expectedResponse: raw.expectedResponse,
		postcondition: raw.postcondition,
	};
}

export function mapGraphQLResultsTestCaseSteps(raw: GraphQLResultsTestCaseStepOutput[] | null | undefined): ResultsTestCaseStep[] {
	return (raw ?? []).map(mapGraphQLResultsTestCaseStep);
}

/**
 * Maps the raw resultsTestResult response to the objects under test (ECUs + offboard components).
 * @param raw
 */
export function mapGraphQLResultsObjectsUnderTest(raw: GraphQLResultsObjectsUnderTestOutput | null | undefined): {
	ecus: ResultsTestResultEcu[];
	offboardComponents: ResultsTestResultOffboardComponent[];
} {
	return {
		ecus: (raw?.ecus ?? []).map(mapObjectUnderTestEcu),
		offboardComponents: (raw?.offboardComponents ?? []).map(mapObjectUnderTestOffboardComponent),
	};
}

function mapObjectUnderTestEcu(raw: GraphQLResultsObjectUnderTestEcuOutput): ResultsTestResultEcu {
	return {
		id: raw.id,
		name: raw.name,
		description: raw.description,
		fpcCondition: raw.fpcCondition,
		familyId: raw.familyId,
		familyName: raw.familyName,
	};
}

function mapObjectUnderTestOffboardComponent(raw: GraphQLResultsObjectUnderTestOffboardComponentOutput): ResultsTestResultOffboardComponent {
	return {
		uid: raw.uid,
		value: raw.value,
		version: raw.version,
		identifier: raw.identifier,
		variant: raw.variant,
	};
}

/**
 * Maps a raw requirement-with-test-analysis row (shared by resultsRequirementsWithTestAnalysis and
 * resultsRequirementsWithTestAnalysisByTestCase) to the app-level ResultsRequirementRow.
 * @param raw
 */
export function mapGraphQLResultsRequirementRow(raw: GraphQLResultsRequirementRowOutput): ResultsRequirementRow {
	return {
		requirement: {
			requirementId: raw.requirement.requirementId,
			name: raw.requirement.name,
			version: raw.requirement.version,
			asil: raw.requirement.asil,
			irmaVersion: raw.requirement.irmaVersion,
		},
		testAnalysis: raw.testAnalysis
			? {
					uid: raw.testAnalysis.uid,
					itemNo: raw.testAnalysis.itemNo,
					name: raw.testAnalysis.name,
					version: raw.testAnalysis.version,
					regBy: raw.testAnalysis.regBy,
					regTime: raw.testAnalysis.regTime,
					validTo: raw.testAnalysis.validTo,
				}
			: null,
		testAnalysisAlternative: raw.testAnalysisAlternative
			? {
					uid: raw.testAnalysisAlternative.uid,
					name: raw.testAnalysisAlternative.name,
					version: raw.testAnalysisAlternative.version,
					regBy: raw.testAnalysisAlternative.regBy,
					regTime: raw.testAnalysisAlternative.regTime,
					validTo: raw.testAnalysisAlternative.validTo,
				}
			: null,
	};
}

export function mapGraphQLResultsRequirementRows(raw: GraphQLResultsRequirementRowOutput[] | null | undefined): ResultsRequirementRow[] {
	return (raw ?? []).map(mapGraphQLResultsRequirementRow);
}

/**
 * Maps the raw resultsSopsForTestRun response to the app-level ResultsSopsResponse.
 * @param raw
 */
export function mapGraphQLResultsSops(raw: GraphQLResultsSopsOutput | null | undefined): ResultsSopsResponse | null {
	if (!raw) return null;
	return {
		fileUid: raw.fileUid,
		fileName: raw.fileName,
		versionControlVersion: raw.versionControlVersion,
		fileTime: raw.fileTime,
		importTime: raw.importTime,
		originTypeUid: raw.originTypeUid,
		regBy: raw.regBy,
		regTime: raw.regTime,
		vinNo: raw.vinNo,
		vehicleName: raw.vehicleName,
		fpcs: (raw.fpcs ?? []).map(f => ({
			name: f.name,
			value: f.value,
			familyDescription: f.familyDescription,
			variantDescription: f.variantDescription,
		})),
		xpcs: (raw.xpcs ?? []).map(x => ({ name: x.name, value: x.value })),
		functionParameters: (raw.functionParameters ?? []).map(fp => ({
			family: fp.family,
			parentName: fp.parentName,
			name: fp.name,
			value: fp.value,
		})),
	};
}

/**
 * Maps a raw E2 parameter (resultsEcuParametersForTestRun) to the app-level EcuParamItem.
 * @param raw
 */
export function mapGraphQLResultsEcuParameter(raw: GraphQLResultsEcuParameterOutput): EcuParamItem {
	return {
		name: raw.name,
		description: raw.description,
		value: raw.value,
		enumValue: raw.enumValue,
		valueType: raw.valueType,
		ecuName: raw.ecuName,
		ecuFamily: raw.ecuFamily,
	};
}

export function mapGraphQLResultsEcuParameters(raw: GraphQLResultsEcuParameterOutput[] | null | undefined): EcuParamItem[] {
	return (raw ?? []).map(mapGraphQLResultsEcuParameter);
}

/**
 * Maps a raw test suite alternative (resultsTestSuiteAlternativesForTestSuite) to the app-level
 * ResultsTestCaseAlternative (suite and case alternatives share this shape). Test suite alternatives
 * carry no test objects, and their properties have no uid/variant/area, so those default to empty.
 * @param raw
 */
export function mapGraphQLResultsTestSuiteAlternative(raw: GraphQLResultsTestSuiteAlternativeOutput): ResultsTestCaseAlternative {
	return {
		uid: raw.uid,
		version: raw.version,
		name: raw.name,
		label: raw.label,
		fromDate: raw.fromDate,
		toDate: raw.toDate,
		validFromSop: raw.validFromSop,
		validToSop: raw.validToSop,
		testObjects: [],
		regBy: raw.regBy,
		regTime: raw.regTime,
		properties: (raw.properties ?? []).map(p => ({
			uid: '',
			name: p.name,
			value: p.value,
			valueDescription: '',
			variant: '',
			productCategoryName: '',
			productCategoryUid: '',
		})),
		testGroups: raw.testGroups ?? [],
	};
}

export function mapGraphQLResultsTestSuiteAlternatives(raw: GraphQLResultsTestSuiteAlternativeOutput[] | null | undefined): ResultsTestCaseAlternative[] {
	return (raw ?? []).map(mapGraphQLResultsTestSuiteAlternative);
}

/**
 * Maps a raw resultsTestCaseAlternatives entry to the app-level ResultsTestCaseAlternative model.
 * The alternative's properties carry no uid/variant/area from this query, so those fields default
 * to empty while name/value/valueDescription/label are mapped through.
 * @param raw
 */
export function mapGraphQLResultsTestCaseAlternative(raw: GraphQLResultsTestCaseAlternativeOutput): ResultsTestCaseAlternative {
	return {
		uid: raw.uid,
		version: raw.version,
		name: raw.name,
		label: raw.label,
		fromDate: raw.fromDate,
		toDate: raw.toDate,
		validFromSop: raw.validFromSop,
		validToSop: raw.validToSop,
		testObjects: (raw.testObjects ?? []).map(to => ({ uid: to.uid, name: to.name, typeId: to.typeId })),
		regBy: raw.regBy,
		regTime: raw.regTime,
		properties: (raw.properties ?? []).map(p => ({
			uid: '',
			name: p.name,
			value: p.value,
			valueDescription: p.valueDescription,
			variant: '',
			productCategoryName: '',
			productCategoryUid: '',
		})),
		testGroups: raw.testGroups ?? [],
	};
}

export function mapGraphQLResultsTestCaseAlternatives(raw: GraphQLResultsTestCaseAlternativeOutput[] | null | undefined): ResultsTestCaseAlternative[] {
	return (raw ?? []).map(mapGraphQLResultsTestCaseAlternative);
}

/**
 * Maps the raw resultsTestCaseDetails output to the app-level ResultsTestCase model.
 *
 * Several fields are reshaped at this boundary: UFs are flattened to display strings, ECUs and
 * offboard components to their names/values (the model holds string summaries here), and product
 * properties fold the query's productCategory fields into the model's area fields. Fields not
 * returned by this query (steps, resultCount) default to empty.
 * @param raw
 */
export function mapGraphQLResultsTestCaseDetails(raw: GraphQLResultsTestCaseDetailsOutput): ResultsTestCase {
	return {
		tcUid: raw.tcUid,
		itemNo: raw.itemNo,
		name: raw.name,
		description: raw.description,
		version: raw.version,
		regBy: raw.regBy,
		regTime: raw.regTime,
		steps: [],
		owners: raw.owners ?? [],
		ufs: mapUfs(raw.ufs),
		productProperties: mapProductProperties(raw.productProperties),
		ecus: mapEcus(raw.ecus),
		offboardComponents: mapOffboardComponents(raw.offboardComponents),
		requirement: raw.requirement,
		script: raw.script,
		resultCount: 0,
		alternatives: (raw.alternatives ?? []).map(mapTestCaseDetailsAlternativeRef),
		versions: mapVersions(raw.versions),
		operator: raw.operator ?? '',
		value: raw.value ?? '',
		testCases: [],
	};
}

function mapTestCaseDetailsAlternativeRef(raw: GraphQLResultsTestCaseAlternativeRefOutput): ResultsTestCaseAlternative {
	return {
		uid: raw.uid,
		version: raw.version,
		name: raw.name,
		label: raw.label,
		fromDate: raw.fromDate,
		toDate: raw.toDate,
		validFromSop: raw.validFromSop,
		validToSop: raw.validToSop,
		testObjects: [],
		regBy: raw.regBy,
		regTime: raw.regTime,
		properties: [],
		testGroups: raw.testGroups ?? [],
	};
}

// --- Full detail-page mappers (test suite / case / specification) ---

/**
 * Flattens a versioned item's version rows to the app-level ResultsVersion[].
 * @param raw
 */
function mapVersions(raw: GraphQLResultsVersionOutput[] | null | undefined): ResultsVersion[] {
	return (raw ?? []).map(v => ({
		itemUid: v.itemUid,
		versionNo: v.versionNo,
		validTo: v.validTo,
		regBy: v.regBy,
		regTime: v.regTime,
	}));
}

/**
 * Maps UF rows to the structured ResultsUF shape (ufNumber + name).
 * @param raw
 */
function mapUfs(raw: GraphQLResultsTestCaseUfOutput[] | null | undefined): ResultsUF[] {
	return (raw ?? []).map(uf => ({ ufNumber: uf.ufNumber, name: uf.name }));
}

/**
 * Maps product properties, preserving the raw productCategory fields the detail views render.
 * @param raw
 */
function mapProductProperties(raw: GraphQLResultsTestCaseProductPropertyOutput[] | null | undefined): ResultsTestCaseProductProperty[] {
	return (raw ?? []).map(pp => ({
		uid: pp.uid,
		name: pp.name,
		value: '',
		valueDescription: '',
		variant: pp.variant,
		productCategoryName: pp.productCategoryName,
		productCategoryUid: pp.productCategoryUid,
	}));
}

/**
 * Maps ECU rows to the structured ResultsEcu shape (id/name/description/fpcCondition/family).
 * @param raw
 */
function mapEcus(raw: GraphQLResultsTestResultEcuOutput[] | null | undefined): ResultsEcu[] {
	return (raw ?? []).map(e => ({
		id: e.id,
		name: e.name,
		description: e.description,
		fpcCondition: e.fpcCondition,
		familyId: e.familyId,
		familyName: e.familyName,
	}));
}

/**
 * Maps the raw `ecus` catalogue (ALL_ECUS_QUERY) to ResultsEcu[]. The catalogue rows share the same
 * shape as the ECUs embedded in test cases/results, so this reuses mapEcus.
 * @param raw
 */
export function mapGraphQLResultsAllEcus(raw: GraphQLResultsTestResultEcuOutput[] | null | undefined): ResultsEcu[] {
	return mapEcus(raw);
}

/**
 * Maps offboard components, preserving the structured shape (value/identifier shown in details).
 * @param raw
 */
function mapOffboardComponents(raw: GraphQLResultsTestResultOffboardComponentOutput[] | null | undefined): ResultsOffboardComponent[] {
	return (raw ?? []).map(oc => ({
		uid: oc.uid,
		value: oc.value,
		version: oc.version,
		identifier: oc.identifier,
		variant: oc.variant,
	}));
}

/**
 * Maps the full resultsTestSuite record to the app-level ResultsTestSuite (suite details page).
 * @param raw
 */
export function mapGraphQLResultsTestSuiteFull(raw: GraphQLResultsTestSuiteFullOutput): ResultsTestSuite {
	return {
		testSuiteUid: raw.testSuiteUid,
		itemNo: raw.itemNo,
		name: raw.name,
		version: raw.version,
		owners: raw.owners ?? [],
		ufs: mapUfs(raw.ufs),
		productProperties: mapProductProperties(raw.productProperties),
		offboardComponents: mapOffboardComponents(raw.offboardComponents),
		regTime: raw.regTime,
		regBy: raw.regBy,
		description: raw.description,
		testSuites: [],
		nodeOrder: 0,
		versions: mapVersions(raw.versions),
	};
}

/**
 * Maps the full resultsTestCase record to the app-level ResultsTestCase (test case details page).
 * @param raw
 */
export function mapGraphQLResultsTestCaseFull(raw: GraphQLResultsTestCaseFullOutput): ResultsTestCase {
	return {
		tcUid: raw.tcUid,
		itemNo: raw.itemNo,
		name: raw.name,
		description: raw.description,
		version: raw.version,
		regBy: raw.regBy,
		regTime: raw.regTime,
		steps: (raw.steps ?? []).map(mapGraphQLResultsTestCaseStep),
		owners: raw.owners ?? [],
		ufs: mapUfs(raw.ufs),
		productProperties: mapProductProperties(raw.productProperties),
		ecus: mapEcus(raw.ecus),
		offboardComponents: mapOffboardComponents(raw.offboardComponents),
		requirement: raw.requirement,
		script: raw.script,
		resultCount: 0,
		alternatives: (raw.alternatives ?? []).map(mapGraphQLResultsTestCaseAlternative),
		versions: mapVersions(raw.versions),
		operator: '',
		value: '',
		testCases: [],
	};
}

/**
 * Maps the resultsTestSpecification record to the app-level ResultsTestSpecification.
 * @param raw
 */
export function mapGraphQLResultsTestSpecification(raw: GraphQLResultsTestSpecificationOutput): ResultsTestSpecification {
	return {
		uid: raw.uid,
		itemNo: raw.itemNo,
		name: raw.name,
		version: raw.version,
		owners: raw.owners ?? [],
		regBy: raw.regBy,
		regTime: raw.regTime,
		testAnalyses: (raw.testAnalyses ?? []).map(ta => ({
			uid: ta.uid,
			itemNo: ta.itemNo,
			name: ta.name,
			version: ta.version,
			regBy: ta.regBy,
			regTime: ta.regTime,
			validTo: '',
		})),
		versions: mapVersions(raw.versions),
	};
}
